import React, { useEffect, useMemo, useRef, useState } from 'react';

import uniq from 'lodash/uniq';
import { useSearchParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import { Alert, Box, Button, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridFilterOperator,
  GridLogicOperator,
  GridPaginationModel,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-pro';

import { parseDateRange } from '~/components/GHAParticipationReport/reportSearchParams';

import { useGetCompetitorHotelOpportunities } from '~/hooks/apis/price-parity/useGetCompetitorHotelOpportunities';
import { useGetCompetitorProperties } from '~/hooks/apis/price-parity/useGetCompetitorProperties';

import type { Dayjs } from '~/services/TimeService';

import CompetitorOfferGhaOpportunitiesCell from './CompetitorOfferGhaOpportunitiesCell';
import CompetitorOfferLeFlashCell from './CompetitorOfferLeFlashCell';
import CompetitorOfferPropertyLink from './CompetitorOfferPropertyLink';
import CompetitorOfferSearchingFromCell from './CompetitorOfferSearchingFromCell';
import CompetitorOfferSuppliersPanel from './CompetitorOfferSuppliersPanel';
import CompetitorOffersDateRange from './CompetitorOffersDateRange';
import CompetitorOffersNoResults from './CompetitorOffersNoResults';
import CompetitorOffersPresets from './CompetitorOffersPresets';
import CompetitorOffersSearchFilterInput from './CompetitorOffersSearchFilterInput';
import CompetitorOffersToolbar from './CompetitorOffersToolbar';
import {
  COMPETITOR_FLASH_STATUS_LABELS,
  COMPETITOR_OFFERS_PAGE_SIZE,
  COMPETITOR_SOURCE_LABELS,
  COMPETITOR_SOURCE_NAMES,
  CompetitorFlashStatus,
  CompetitorOffersPreset,
  FLASH_MATCH_LABELS,
  FlashMatch,
  LE_FLASH_STATUS_LABELS,
} from './constants';
import {
  CompetitorOfferRow,
  compareOpportunities,
  competitorFlashStatusOf,
  competitorOfferPath,
  flashMatchOf,
  flashPropertyVendors,
  formatDate,
  hotelSuppliers,
  matchesFlashIds,
  matchesHotelName,
  matchesPropertyIds,
  normaliseIds,
  offerPath,
  pageSearchParams,
  presetOf,
  searchParamsToFilterModel,
  sortByRecentlyCollected,
  supplierKey,
  toDateParam,
  unfilteredFields,
  withPreset,
} from './utils';

// Filter columns, hidden until shown from the Columns panel.
const HIDDEN_COLUMNS: GridColumnVisibilityModel = {
  lePropertyId: false,
  leOfferIds: false,
  sourceName: false,
  onCompetitor: false,
  matchedToFlash: false,
};

const hotelNameFilterOperators: Array<GridFilterOperator<CompetitorOfferRow>> = [
  {
    label: 'contains',
    value: 'contains',
    getApplyFilterFn: (item) =>
      typeof item.value === 'string' && item.value.trim() ? (_, row) => matchesHotelName(row, item.value) : null,
    InputComponent: CompetitorOffersSearchFilterInput,
  },
];

// Exact ids, several at once.
const idFilterOperators = (
  matches: (property: CompetitorOfferRow, ids: Array<string>) => boolean,
): Array<GridFilterOperator<CompetitorOfferRow>> => [
  {
    label: 'is any of',
    value: 'isAnyOf',
    getApplyFilterFn: (item) => {
      const ids = normaliseIds(item.value);
      return ids.length ? (_, row) => matches(row, ids) : null;
    },
    InputComponent: CompetitorOffersSearchFilterInput,
    InputComponentProps: { multiple: true },
  },
];

// Hotel name and Details are always shown.
const ALWAYS_SHOWN_FIELDS = ['hotelName', 'details'];

const singleSelectOperators = (operator: string) =>
  getGridSingleSelectOperators().filter((candidate) => candidate.value === operator);

const columns: Array<GridColDef<CompetitorOfferRow>> = [
  {
    field: 'hotelName',
    headerName: 'Hotel name',
    width: 240,
    valueGetter: (value, row) => value ?? row.externalPropertyId,
    filterOperators: hotelNameFilterOperators,
    renderCell: ({ row, value }) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[COMPETITOR_SOURCE_LABELS[row.sourceName], row.location].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'lePropertyId',
    headerName: 'Property ID',
    width: 300,
    valueGetter: (_, row) => row.lePropertyIds.join(' '),
    filterOperators: idFilterOperators(matchesPropertyIds),
    renderCell: ({ row }) => (
      <Stack>
        {[...flashPropertyVendors(row)].map(([propertyId, vendorId]) => (
          <CompetitorOfferPropertyLink key={propertyId} propertyId={propertyId} vendorId={vendorId} />
        ))}
      </Stack>
    ),
  },
  {
    field: 'leOfferIds',
    headerName: 'Flash ID',
    width: 200,
    valueGetter: (_, row) => row.leOffers.map((offer) => offer.offerId).join(' '),
    filterOperators: idFilterOperators(matchesFlashIds),
    renderCell: ({ row }) => (
      <Stack>
        {uniq(row.leOffers.map((offer) => offer.offerId)).map((offerId) => (
          <MuiLink
            key={offerId}
            component={Link}
            to={offerPath(offerId)}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
          >
            {offerId}
          </MuiLink>
        ))}
      </Stack>
    ),
  },
  {
    field: 'onLe',
    headerName: 'On LE (Flash)',
    type: 'singleSelect',
    width: 190,
    valueGetter: (_, row) => row.leFlashStatus,
    valueOptions: Object.entries(LE_FLASH_STATUS_LABELS).map(([value, label]) => ({ value, label })),
    filterOperators: singleSelectOperators('is'),
    renderCell: ({ row }) => <CompetitorOfferLeFlashCell property={row} />,
  },
  {
    field: 'ghaOpportunities',
    headerName: 'Opportunities',
    description: 'Google Hotel Ads opportunities in the date range for the matched Flash hotel, LPP and LPC together',
    type: 'number',
    width: 150,
    filterable: false,
    sortable: true,
    valueGetter: (_, row) => (row.gha.status === 'loaded' ? row.gha.opportunities?.opportunities : undefined),
    getSortComparator: (direction) => (a, b) => compareOpportunities(a, b, direction ?? 'desc'),
    renderCell: ({ row }) => <CompetitorOfferGhaOpportunitiesCell row={row} />,
  },
  {
    field: 'searchingFrom',
    headerName: 'Searching from',
    description: 'Where users searched from in the date range; hover for every country',
    width: 220,
    filterable: false,
    renderCell: ({ row }) => <CompetitorOfferSearchingFromCell row={row} />,
  },
  {
    field: 'lastCollectedAt',
    headerName: 'Last collected',
    type: 'date',
    width: 190,
    filterable: false,
    valueGetter: (value) => (value ? new Date(value) : null),
    renderCell: ({ row }) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {formatDate(row.lastCollectedAt)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.lastCollectedAt ? dayjs(row.lastCollectedAt).fromNow() : 'Never collected'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'details',
    headerName: '',
    width: 110,
    filterable: false,
    renderCell: ({ row }) => (
      <Button size="small" variant="outlined" component={Link} to={competitorOfferPath(row)}>
        Details
      </Button>
    ),
  },
  {
    field: 'sourceName',
    headerName: 'Competitor',
    type: 'singleSelect',
    valueOptions: COMPETITOR_SOURCE_NAMES.map((source) => ({ value: source, label: COMPETITOR_SOURCE_LABELS[source] })),
    filterOperators: singleSelectOperators('isAnyOf'),
  },
  {
    field: 'onCompetitor',
    headerName: 'On competitor (Flash)',
    type: 'singleSelect',
    valueGetter: (_, row) => competitorFlashStatusOf(row),
    valueOptions: Object.values(CompetitorFlashStatus).map((status) => ({
      value: status,
      label: COMPETITOR_FLASH_STATUS_LABELS[status],
    })),
    filterOperators: singleSelectOperators('is'),
  },
  {
    field: 'matchedToFlash',
    headerName: 'Matched to Flash',
    type: 'singleSelect',
    valueGetter: (_, row) => flashMatchOf(row),
    valueOptions: Object.values(FlashMatch).map((match) => ({ value: match, label: FLASH_MATCH_LABELS[match] })),
    filterOperators: singleSelectOperators('is'),
  },
];

// Only Opportunities sorts; the list otherwise stays latest collected first.
columns.forEach((column) => {
  column.sortable = column.sortable ?? false;
});

const TOGGLABLE_FIELDS = columns.map((column) => column.field).filter((field) => !ALWAYS_SHOWN_FIELDS.includes(field));

export default function CompetitorOffersContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => searchParamsToFilterModel(searchParams));
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => parseDateRange(searchParams));
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: COMPETITOR_OFFERS_PAGE_SIZE,
  });
  // The query string this page last wrote, so only outside URL changes reload the filters.
  const writtenSearch = useRef(searchParams.toString());
  const { data: properties = [], isLoading, error } = useGetCompetitorProperties();
  const opportunities = useGetCompetitorHotelOpportunities(toDateParam(dateRange[0]), toDateParam(dateRange[1]));

  const rows = useMemo((): Array<CompetitorOfferRow> => {
    const byHotel = new Map(opportunities.data?.map((hotel) => [hotel.hotelKey, hotel]));
    return sortByRecentlyCollected(properties).map((property) => ({
      ...property,
      gha: opportunities.data
        ? { status: 'loaded', opportunities: property.hotelKey ? byHotel.get(property.hotelKey) : undefined }
        : { status: opportunities.isError ? 'error' : 'loading' },
    }));
  }, [properties, opportunities.data, opportunities.isError]);

  useEffect(() => {
    const search = searchParams.toString();
    if (search !== writtenSearch.current) {
      writtenSearch.current = search;
      setFilterModel(searchParamsToFilterModel(searchParams));
      setDateRange(parseDateRange(searchParams));
    }
  }, [searchParams]);

  const writeSearchParams = (nextFilterModel: GridFilterModel, nextDateRange: [Dayjs, Dayjs]) => {
    const nextSearchParams = pageSearchParams(nextFilterModel, nextDateRange);
    if (nextSearchParams.toString() !== writtenSearch.current) {
      writtenSearch.current = nextSearchParams.toString();
      setSearchParams(nextSearchParams, { replace: true });
    }
  };

  const handleFilterModelChange = (nextFilterModel: GridFilterModel) => {
    setFilterModel(nextFilterModel);
    setPaginationModel((current) => ({ ...current, page: 0 }));
    writeSearchParams(nextFilterModel, dateRange);
  };

  const handleDateRangeChange = (nextDateRange: [Dayjs, Dayjs]) => {
    setDateRange(nextDateRange);
    writeSearchParams(filterModel, nextDateRange);
  };

  const handlePresetChange = (preset: CompetitorOffersPreset) =>
    handleFilterModelChange(withPreset(filterModel, preset));

  const handleCustom = () => {
    if (presetOf(filterModel)) {
      handleFilterModelChange(withPreset(filterModel, undefined));
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Hotels collected from competitor sites, matched to our flash properties. Prices are in the competitor&apos;s
        currency, with AUD at the rate of the day they were collected. Collected once a day; room prices and terms are
        not rechecked against the live site.
      </Alert>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap gap={2}>
        <CompetitorOffersPresets filterModel={filterModel} onChange={handlePresetChange} onCustom={handleCustom} />
        <CompetitorOffersDateRange value={dateRange} onChange={handleDateRangeChange} />
      </Stack>

      <Box mt={2}>
        {error && <Alert severity="error">Failed to load competitor offers: {error.message}</Alert>}
        {!error && opportunities.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Couldn&apos;t load Google Hotel Ads opportunities: {opportunities.error.message}
          </Alert>
        )}
        {!error && (
          <Paper variant="outlined">
            <DataGridPro
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(row: CompetitorOfferRow) => supplierKey(row)}
              loading={isLoading}
              getRowHeight={() => 'auto'}
              initialState={{ columns: { columnVisibilityModel: HIDDEN_COLUMNS } }}
              disableColumnMenu
              sortingOrder={['desc', 'asc', null]}
              disableRowSelectionOnClick
              pagination
              pageSizeOptions={[COMPETITOR_OFFERS_PAGE_SIZE]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              getDetailPanelContent={({ row }) => (
                <CompetitorOfferSuppliersPanel suppliers={hotelSuppliers(properties, row)} />
              )}
              getDetailPanelHeight={() => 'auto'}
              slots={{ toolbar: CompetitorOffersToolbar, noResultsOverlay: CompetitorOffersNoResults }}
              slotProps={{
                columnsManagement: { getTogglableColumns: () => TOGGLABLE_FIELDS },
                filterPanel: {
                  logicOperators: [GridLogicOperator.And],
                  // One filter per column: "is any of" covers several values, and a second AND filter could only narrow it.
                  getColumnForNewFilter: ({ currentFilters }) => unfilteredFields(currentFilters)[0] ?? null,
                  filterFormProps: {
                    filterColumns: ({ field, currentFilters }) => [field, ...unfilteredFields(currentFilters)],
                    // AND is the only option, so the disabled "And" select is noise.
                    logicOperatorInputProps: { sx: { display: 'none' } },
                    columnInputProps: { sx: { width: 240 } },
                    valueInputProps: { sx: { width: 420 }, InputComponentProps: { limitTags: 3 } },
                  },
                },
              }}
              sx={{ border: 0, '& .MuiDataGrid-cell': { py: 1.5, display: 'flex', alignItems: 'center' } }}
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
}
